import type { FC} from 'react';
import { useState, useEffect, useCallback } from 'react'
import type { CallBackProps} from 'react-joyride';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride'
import { TOUR_STEPS, TOUR_COMPLETED_KEY } from './tourSteps'

interface GuidedTourProps {
  isActive: boolean
  onComplete: () => void
  onTabChange?: (tabId: string) => void
  onTourComplete?: () => void // Called when tour finishes/skips - use to sync external state
}

/**
 * Guided tour component using react-joyride
 *
 * Features:
 * - 10 steps covering all settings tabs
 * - Completion tracking via localStorage
 * - Skip/restart controls
 * - Auto-navigation between tabs as tour progresses
 */
export const GuidedTour: FC<GuidedTourProps> = ({
  isActive,
  onComplete,
  onTabChange,
  onTourComplete
}) => {
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)

  // Mark tour as completed
  const markTourCompleted = useCallback(() => {
    localStorage.setItem(TOUR_COMPLETED_KEY, 'true')
  }, [])

  // Start tour when activated (unless already completed)
  useEffect(() => {
    if (isActive) {
      setStepIndex(0)
      setRun(true)
    }
  }, [isActive])

  // Handle tour callbacks
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type } = data

    // Handle step changes - navigate to appropriate tab
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextStep = TOUR_STEPS[index + 1]
      if (nextStep && onTabChange) {
        // Extract tab id from target selector
        const match = nextStep.target.toString().match(/data-tour="tab-(\w+)"/)
        if (match) {
          onTabChange(match[1])
        }
      }
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1))
    }

    // Handle tour completion or skip
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status as typeof STATUS.FINISHED)) {
      setRun(false)
      markTourCompleted()
      onTourComplete?.() // Notify parent so hook state updates immediately
      onComplete()
    }
  }, [onComplete, onTabChange, markTourCompleted, onTourComplete])

  return (
    <Joyride
      steps={TOUR_STEPS}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep
      disableOverlayClose
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#2563eb',
          zIndex: 10000,
          arrowColor: '#fff',
          backgroundColor: '#fff',
          textColor: '#1f2937'
        },
        tooltipContainer: {
          textAlign: 'left'
        },
        buttonNext: {
          backgroundColor: '#2563eb',
          borderRadius: '6px',
          padding: '8px 16px'
        },
        buttonBack: {
          color: '#4b5563',
          marginRight: 8
        },
        buttonSkip: {
          color: '#9ca3af'
        }
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour'
      }}
      floaterProps={{
        disableAnimation: false
      }}
    />
  )
}

export default GuidedTour
